import { Badge, SearchInput, Surface } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import type { ArticleListItem } from '@warehouse/shared';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { listArticles } from '../api';

/**
 * Server-searched article picker.
 *
 * Deliberately NOT a `<Select>` full of options: the register is expected to
 * hold tens of thousands of articles, and a select that loads them all is a
 * warehouse tablet that stops responding. Search runs through the same
 * server-side `q` every list uses, so what the picker finds is exactly what the
 * article list finds.
 */
export function ArticlePicker({
  onSelect,
  selected,
}: {
  onSelect: (article: ArticleListItem) => void;
  selected?: ArticleListItem | null;
}): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const [q, setQ] = useState('');

  const { data, isFetching } = useQuery({
    queryKey: ['master-data', 'article-picker', q],
    queryFn: () => listArticles({ q: q || undefined, page: 1, pageSize: 20, sort: 'articleNo' }),
    enabled: q.trim().length > 0,
  });

  return (
    <div className="flex flex-col gap-2">
      <SearchInput
        variant="outline"
        value={q}
        onChange={setQ}
        onClear={() => {
          setQ('');
        }}
        placeholder={t('masterData:articles.searchPlaceholder')}
        aria-label={t('masterData:articles.searchPlaceholder')}
      />

      {selected ? (
        <p className="text-sm text-ink">
          <span className="font-mono">{selected.articleNo}</span> — {selected.name}
        </p>
      ) : null}

      {q.trim().length > 0 ? (
        <Surface variant="subtle" className="max-h-64 overflow-y-auto scrollbar-thin">
          {(data?.items ?? []).length === 0 && !isFetching ? (
            <p className="p-3 text-sm text-ink-muted">{t('common:common.noResults')}</p>
          ) : (
            <ul className="divide-y divide-surface-border">
              {(data?.items ?? []).map((article) => (
                <li key={article.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-brand-600/[0.06]"
                    onClick={() => {
                      onSelect(article);
                      setQ('');
                    }}
                  >
                    <span className="font-mono text-xs text-ink-muted">{article.articleNo}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-ink">{article.name}</span>
                    {!article.isActive ? (
                      <Badge tone="warning" size="sm">
                        {t('common:common.inactive')}
                      </Badge>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Surface>
      ) : null}
    </div>
  );
}
